import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  MobileStepper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupIcon from '@mui/icons-material/Group';
import LuggageIcon from '@mui/icons-material/Luggage';
import WifiIcon from '@mui/icons-material/Wifi';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const FleetHero = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.85)), url("/images/fleet.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '50vh',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.common.white,
  [theme.breakpoints.down('md')]: {
    height: '40vh',
  },
}));

const VehicleCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
  },
}));

const vehicles = [
  {
    id: 1,
    name: 'Mercedes-Maybach V-Class Voyager',
    images: [
      '/images/vehicles/maybach-v-class/interior-2.jpg'
    ],
    description: 'Experience unparalleled luxury with our flagship Mercedes-Maybach V-Class Voyager. This exclusive vehicle combines the sophistication of Maybach with the versatility of the V-Class.',
    capacity: 4,
    luggage: 4,
    price: '£170',
    features: [
      'Cream Leather Interior',
      '42 Inch TV',
      'Starlight roof',
      'Roof Ambient Lighting',
      'Mini fridge',
      'Climate Control',
      'Entertainment System',
      'USB Charging Ports',
    ],
  },
  {
    id: 2,
    name: 'Mercedes V-Class Tiffany Blue Edition',
    images: [
      '/images/vehicles/tiffany-v-class/exterior-1.jpg',
      '/images/vehicles/tiffany-v-class/interior-1.jpg',
      '/images/vehicles/tiffany-v-class/interior-2.jpg',
      '/images/vehicles/tiffany-v-class/seats.jpg',
      '/images/vehicles/tiffany-v-class/features.jpg',
    ],
    description: 'Our unique Tiffany Blue V-Class offers a perfect blend of style and comfort. This custom-finished vehicle stands out with its distinctive color and premium features.',
    capacity: 5,
    luggage: 4,
    price: '£170',
    features: [
      'Tiffany Blue Leather Interior',
      '42 Inch TV',
      'Starlight roof',
      'Mini fridge',
      'Climate Control',
      'Ambient Lighting',
      'Entertainment System',
      'USB Charging Ports',
    ],
  },
];

const ImageGalleryDialog = ({ open, onClose, images, currentIndex, onNext, onPrev }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.dark',
          position: 'relative',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'common.white',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0, bgcolor: 'background.dark' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={images[currentIndex]}
            alt={`Gallery image ${currentIndex + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
          <IconButton
            onClick={onPrev}
            sx={{
              position: 'absolute',
              left: 16,
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton
            onClick={onNext}
            sx={{
              position: 'absolute',
              right: 16,
              color: 'common.white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Box>
        <MobileStepper
          steps={images.length}
          position="static"
          activeStep={currentIndex}
          sx={{
            bgcolor: 'background.dark',
            color: 'common.white',
            '& .MuiMobileStepper-dot': {
              bgcolor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiMobileStepper-dotActive': {
              bgcolor: 'common.white',
            },
          }}
          nextButton={null}
          backButton={null}
        />
      </DialogContent>
    </Dialog>
  );
};

const FleetPage = () => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleOpenGallery = (vehicle, imageIndex) => {
    setSelectedVehicle(vehicle);
    setCurrentImageIndex(imageIndex);
  };

  const handleCloseGallery = () => {
    setSelectedVehicle(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedVehicle) {
      setCurrentImageIndex((prev) => 
        prev === selectedVehicle.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (selectedVehicle) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedVehicle.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <Box>
      <FleetHero>
        <Container>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Our Luxury Fleet
          </Typography>
          <Typography 
            variant="h5"
            sx={{
              maxWidth: '600px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            Experience the perfect blend of luxury, comfort, and sophistication with our exclusive Mercedes V-Class vehicles
          </Typography>
        </Container>
      </FleetHero>

      <Container sx={{ py: { xs: 6, md: 12 } }}>
        <Grid container spacing={6}>
          {vehicles.map((vehicle) => (
            <Grid item xs={12} md={6} key={vehicle.id}>
              <VehicleCard>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="400"
                    image={vehicle.images[0]}
                    alt={vehicle.name}
                    sx={{ 
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpenGallery(vehicle, 0)}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {vehicle.name}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph
                    sx={{ mb: 3 }}
                  >
                    {vehicle.description}
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon />
                        <Typography>{vehicle.capacity} Passengers</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LuggageIcon />
                        <Typography>{vehicle.luggage} Luggage</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WifiIcon />
                        <Typography>WiFi</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AcUnitIcon />
                        <Typography>Climate Control</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    Features
                  </Typography>
                  <List dense>
                    {vehicle.features.map((feature, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleOutlineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" component="p" color="primary">
                      From {vehicle.price} per hour
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/booking"
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: 'common.black',
                        color: 'common.white',
                        '&:hover': {
                          bgcolor: 'grey.900',
                        },
                      }}
                    >
                      Book Now
                    </Button>
                  </Box>
                </CardContent>
              </VehicleCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <ImageGalleryDialog
        open={!!selectedVehicle}
        onClose={handleCloseGallery}
        images={selectedVehicle?.images || []}
        currentIndex={currentImageIndex}
        onNext={handleNextImage}
        onPrev={handlePrevImage}
      />
    </Box>
  );
};

export default FleetPage; 